/**
 * /e2-summit — the E2 Summit wall (sidebar: Data Management).
 *
 * A glance-and-go TV view: nothing on it needs reading. Three things only:
 *   - headsets in the field, one box per headset, lit while it is recording
 *     (footage in the last 3 days, Customer Management's "not recording" rule);
 *   - hours of E2 footage on a big odometer, with a bar to the next camp
 *     (log-spaced camps from 100 h toward the 100,000 h summit);
 *   - customers ranked by all-time hours, with the same headset boxes and
 *     when they last recorded. Clicking one opens it in Customer Management,
 *     which holds the detail (weekly hours, pace, emails).
 *
 * Every number comes from GET /api/e2/wall (backend/e2_summit.py). The only
 * thing invented on this page is the odometer's creep between polls: it rolls
 * forward at the 7-day pace, never more than CREEP_CAP_HOURS past the last
 * server total and never backward, so the wall is never still but never
 * claims footage that has not landed.
 *
 * Celebrations fire once per crossing and are remembered in localStorage, so
 * a refresh or a second TV does not replay them. On the very first load the
 * current state is recorded silently: nothing that was already true fires.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMaximize, FiMinimize, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { API_BASE_URL } from '../config';
import './E2SummitTV.css';

// ------------------------------------------------------------------ types --
interface Camp { at: number; done: boolean; next: boolean; }
interface E2Device {
  id: string; org: string; total_hours: number; week_hours: number; pace: number;
  days: number[]; day_labels: string[]; best_day: number; summit_days: number;
  first_recording: string | null; last_recording: string | null; recordings: number;
  never_recorded?: boolean; silent_days?: number | null;
}
interface E2Org {
  org: string; headsets: string[]; headset_count: number; days: number[];
  total_hours: number; week_hours: number; prev_week_hours: number; week_target_hours: number;
  pace: number; active_week: number; at_pace: number; last_recording: string | null;
  silent_days: number | null; top: { id: string; week_hours: number; pace: number } | null;
}
interface E2Week { week_start: string; hours: number; active_headsets: number; partial: boolean; }
interface E2Payload {
  status: 'ok' | 'warming';
  generatedAt: string; today: string; since: string | null;
  target: { pace_hours_per_day: number; week_hours_per_headset: number; summit_hours: number; camps: Camp[]; flags: Camp[] };
  headsets: { customer: number; internal: number; orgs: number; next_flag: number; to_next_flag: number; active_week: number; at_pace_week: number };
  hours: {
    total: number; recordings: number; week: number; prev_week: number; last30: number;
    rate7: number; rate30: number; accel_pct: number | null; fleet_pace: number; summit_days_week: number;
    next_camp: number; to_next_camp: number; days_to_next_camp: number | null; days_to_next_camp_at_pace: number | null;
    at_pace_week_hours: number; best_week: number; longest_recording: number;
  };
  weeks: E2Week[]; orgs: E2Org[]; devices: E2Device[];
  /** Customer names as the console and the weekly emails show them. */
  labels?: Record<string, string>;
}
type CelebKind = 'camp' | 'flag' | 'pace' | 'orgpace' | 'record';
interface Celeb { key: string; kind: CelebKind; title: string; sub: string; }
interface Toast { key: string; title: string; sub: string; }

// -------------------------------------------------------------- constants --
const POLL_MS = 30_000;
const CELEB_MS = 6_000;
const TOAST_MS = 6_000;
const CREEP_CAP_HOURS = 0.25;
const LS_DONE = 'e2-summit-celebrated';
const LS_SEEN = 'e2-summit-seen-headsets';
const LS_SOUND = 'e2-summit-sound';

// ---------------------------------------------------------------- helpers --
const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
const fmt1 = (n: number) => n.toFixed(1);
/** "TomasHomeServices" -> "Tomas Home Services"; names with spaces stay as stored. */
// Same "not recording" rule as Customer Management's default: no footage in 3+ days.
const SILENT_DAYS = 3;
// Customer names come from the server (a "Name in emails" saved in the console wins);
// the camel-case split is only the fallback for an org the server has not named.
let LABELS: Record<string, string> = {};
const orgLabel = (s: string) => LABELS[s] || (s.includes(' ') ? s : s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]+/g, ' '));
const drillUrl = (org: string) => `/customer-management?org=${encodeURIComponent(org)}`;
const isRecording = (d: E2Device) => !d.never_recorded && !!d.last_recording && (d.silent_days ?? Infinity) < SILENT_DAYS;
const lastSeen = (silent: number | null, last: string | null) =>
  !last || silent == null ? 'No footage yet' : silent === 0 ? 'Today' : silent === 1 ? 'Yesterday' : `${silent} days ago`;
const campName = (h: number) => (h === 100 ? 'BASE CAMP' : h >= 100_000 ? 'THE SUMMIT' : `${fmt(h)} HOURS`);
const loadJSON = <T,>(k: string, d: T): T => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) as T : d; } catch { return d; } };
const saveJSON = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* best effort */ } };
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

let audioCtx: AudioContext | null = null;
const playTone = (freqs: number[], dur = 0.12, type: OscillatorType = 'triangle', gain = 0.08) => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || (window as any).webkitAudioContext)();
    const ctx = audioCtx!;
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = type; o.frequency.value = f;
      g.gain.setValueAtTime(gain, ctx.currentTime + i * dur);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (i + 1) * dur);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * dur);
      o.stop(ctx.currentTime + (i + 1) * dur + 0.02);
    });
  } catch { /* audio is best-effort */ }
};
const SOUND: Record<CelebKind | 'toast', () => void> = {
  camp: () => playTone([523, 659, 784, 1047, 1319], 0.12),
  flag: () => playTone([392, 523, 659, 880], 0.1, 'square', 0.07),
  pace: () => playTone([659, 784, 988, 1319], 0.09, 'sawtooth', 0.07),
  orgpace: () => playTone([523, 659, 784, 1047, 1319, 1568], 0.1),
  record: () => playTone([784, 988, 1175], 0.1, 'square', 0.06),
  toast: () => playTone([988, 1319], 0.1, 'square', 0.06),
};

// --------------------------------------------------------------- odometer --
const DRUM_CELLS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
const STEP = 100 / DRUM_CELLS.length;

/** One whole-number drum. A 9 -> 0 roll runs to the spare 0 and snaps back. */
const Drum: React.FC<{ digit: number; dim: boolean; flashOnChange: boolean; tenth?: boolean }> = ({ digit, dim, flashOnChange, tenth }) => {
  const strip = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const prev = useRef(digit);
  useEffect(() => {
    const el = strip.current; if (!el) return;
    const from = prev.current; prev.current = digit;
    if (from === digit) { el.style.transform = `translateY(-${digit * STEP}%)`; return; }
    if (digit < from) {
      el.style.transform = `translateY(-${10 * STEP}%)`;
      const t = setTimeout(() => {
        el.style.transition = 'none'; el.style.transform = 'translateY(0)';
        void el.offsetHeight; el.style.transition = '';
        if (digit > 0) el.style.transform = `translateY(-${digit * STEP}%)`;
      }, 720);
      return () => clearTimeout(t);
    }
    el.style.transform = `translateY(-${digit * STEP}%)`;
    if (flashOnChange && box.current && !reduceMotion()) {
      box.current.classList.remove('e2-spin'); void box.current.offsetWidth; box.current.classList.add('e2-spin');
    }
  }, [digit, flashOnChange]);
  return (
    <div ref={box} className={`e2-drum${dim ? ' e2-dim' : ''}${tenth ? ' e2-tenth' : ''}`}>
      <div ref={strip} className="e2-strip">{DRUM_CELLS.map((n, i) => <i key={i}>{n}</i>)}</div>
    </div>
  );
};

const Odometer: React.FC<{ value: number }> = ({ value }) => {
  // Work in whole tenths so every drum, the tenths included, rests on a digit.
  const tenths = Math.floor(value * 10 + 1e-6);
  const whole = Math.floor(tenths / 10);
  const s = String(Math.min(whole, 999_999)).padStart(6, '0');
  let lead = true;
  const drums = s.split("").map((ch, i) => {
    lead = lead && ch === '0' && i < 5;
    return <Drum key={i} digit={+ch} dim={lead} flashOnChange={i <= 3} />;
  });
  return (
    <div className="e2-drums" aria-label={`${(tenths / 10).toFixed(1)} hours`} role="img">
      {drums.slice(0, 3)}<span className="e2-comma">,</span>{drums.slice(3)}
      <span className="e2-dot">.</span>
      <Drum digit={tenths % 10} dim={false} flashOnChange={false} tenth />
    </div>
  );
};

// ---------------------------------------------------------------- the wall --
const E2SummitTV: React.FC = () => {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<E2Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [display, setDisplay] = useState(0);
  const [celeb, setCeleb] = useState<Celeb | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [rollPulse, setRollPulse] = useState(0);
  const [sound, setSound] = useState(() => loadJSON(LS_SOUND, false));
  const [fullscreen, setFullscreen] = useState(false);
  const [showTests, setShowTests] = useState(false);
  const [seen, setSeen] = useState<Record<string, number>>(() => loadJSON(LS_SEEN, {}));
  const queue = useRef<Celeb[]>([]);
  const toastQueue = useRef<Toast[]>([]);
  const soundRef = useRef(sound);
  useEffect(() => { soundRef.current = sound; saveJSON(LS_SOUND, sound); }, [sound]);

  // ---- data ----
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/e2/wall`);
      if (!res.ok) throw new Error(`The E2 feed answered ${res.status}.`);
      const d: E2Payload = await res.json();
      if (d.status === 'ok') { LABELS = d.labels || {}; setData(d); setError(null); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The E2 feed is unreachable.');
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);
  // While warming, ask again soon instead of waiting a full poll.
  useEffect(() => {
    if (data) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);

  // ---- odometer creep: never backward, never more than CREEP_CAP past the server ----
  const serverTotal = data?.hours.total ?? 0;
  const rate7 = data?.hours.rate7 ?? 0;
  const anchor = useRef({ value: 0, at: 0 });
  useEffect(() => {
    if (!data) return;
    anchor.current = { value: Math.max(serverTotal, anchor.current.value), at: Date.now() };
    if (!display) setDisplay(serverTotal);
  }, [data, serverTotal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data || reduceMotion()) { setDisplay(v => Math.max(v, serverTotal)); return; }
    const t = setInterval(() => {
      const a = anchor.current;
      const projected = a.value + (rate7 / 86_400) * ((Date.now() - a.at) / 1000);
      setDisplay(v => Math.max(v, Math.min(projected, serverTotal + CREEP_CAP_HOURS)));
    }, 1000);
    return () => clearInterval(t);
  }, [data, serverTotal, rate7]);
  const odoRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = odoRef.current;
    if (!rollPulse || !el || reduceMotion()) return;
    el.classList.remove('e2-roll'); void el.offsetWidth; el.classList.add('e2-roll');
  }, [rollPulse]);
  const lastHundred = useRef<number | null>(null);
  useEffect(() => {
    const h = Math.floor(display / 100);
    if (lastHundred.current !== null && h > lastHundred.current) setRollPulse(p => p + 1);
    lastHundred.current = h;
  }, [display]);

  // ---- celebrations: detect crossings against the remembered set ----
  const fire = useCallback((c: Celeb) => { queue.current.push(c); setCeleb(cur => cur ?? queue.current.shift() ?? null); }, []);
  useEffect(() => {
    if (!celeb) return;
    if (soundRef.current) SOUND[celeb.kind]();
    const t = setTimeout(() => setCeleb(queue.current.shift() ?? null), CELEB_MS);
    return () => clearTimeout(t);
  }, [celeb]);
  const pushToast = useCallback((t: Toast) => { toastQueue.current.push(t); setToast(cur => cur ?? toastQueue.current.shift() ?? null); }, []);
  useEffect(() => {
    if (!toast) return;
    if (soundRef.current) SOUND.toast();
    const t = setTimeout(() => setToast(toastQueue.current.shift() ?? null), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!data) return;
    const firstEver = localStorage.getItem(LS_DONE) === null;
    const done = new Set(loadJSON<string[]>(LS_DONE, []));
    const fresh: Celeb[] = [];
    const add = (c: Celeb) => { if (!done.has(c.key)) { done.add(c.key); fresh.push(c); } };
    const wk = data.weeks.length ? data.weeks[data.weeks.length - 1].week_start : data.today;
    data.target.camps.filter(c => c.done).forEach(c =>
      add({ key: `camp:${c.at}`, kind: 'camp', title: `⛺ ${campName(c.at)}`, sub: `${fmt(c.at)} hours of E2 footage` }));
    data.target.flags.filter(f => f.done).forEach(f =>
      add({ key: `flag:${f.at}`, kind: 'flag', title: `🚩 ${f.at} HEADSETS`, sub: 'E2 headsets in the field' }));
    data.devices.filter(d => d.week_hours >= data.target.week_hours_per_headset).forEach(d =>
      add({ key: `pace:${d.id}:${wk}`, kind: 'pace', title: '🔥 SUMMIT PACE', sub: `${orgLabel(d.org)} · headset ${d.id.slice(-4)} held 3 h/day for a week` }));
    data.orgs.filter(o => o.pace >= data.target.pace_hours_per_day).forEach(o =>
      add({ key: `orgpace:${o.org}:${wk}`, kind: 'orgpace', title: '🏔️ ORG AT SUMMIT PACE', sub: `${orgLabel(o.org)} averaged 3 h/day on every headset` }));
    const done_weeks = data.weeks.filter(w => !w.partial);
    if (done_weeks.length > 1) {
      const last = done_weeks[done_weeks.length - 1];
      if (last.hours > 0 && last.hours >= Math.max(...done_weeks.map(w => w.hours)))
        add({ key: `record:${last.week_start}`, kind: 'record', title: '📈 BEST WEEK EVER', sub: `${fmt1(last.hours)} hours of E2 footage in one week` });
    }
    saveJSON(LS_DONE, Array.from(done));
    if (!firstEver) fresh.forEach(fire);

    // New headsets: first sighting gets a toast (except on the very first load).
    const now = Date.now(), nextSeen = { ...seen };
    let changed = false;
    data.devices.forEach(d => {
      if (nextSeen[d.id]) return;
      nextSeen[d.id] = firstEver ? 1 : now; changed = true;
      if (!firstEver) pushToast({ key: d.id, title: 'New E2 online', sub: `${orgLabel(d.org)} · headset ${d.id.slice(-4)} just joined the climb` });
    });
    if (changed) { setSeen(nextSeen); saveJSON(LS_SEEN, nextSeen); }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- fullscreen ----
  useEffect(() => {
    const on = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else rootRef.current?.requestFullscreen?.();
  };

  // ---- leaderboard FLIP ----
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastTops = useRef<Record<string, number>>({});
  const orgs = useMemo(() => [...(data?.orgs ?? [])].sort((a, b) => b.total_hours - a.total_hours || a.org.localeCompare(b.org)), [data]);
  useLayoutEffect(() => {
    const tops: Record<string, number> = {};
    Object.entries(rowRefs.current).forEach(([k, el]) => { if (el) tops[k] = el.getBoundingClientRect().top; });
    if (!reduceMotion()) {
      Object.entries(tops).forEach(([k, top]) => {
        const before = lastTops.current[k], el = rowRefs.current[k];
        if (before != null && el && before !== top)
          el.animate([{ transform: `translateY(${before - top}px)` }, { transform: 'none' }], { duration: 900, easing: 'cubic-bezier(.2,.9,.3,1)' });
      });
    }
    lastTops.current = tops;
  }, [orgs]);

  // ---- previews (🎬) ----
  const previews: { label: string; run: () => void }[] = [
    { label: '⛺ Camp', run: () => fire({ key: `t${Date.now()}`, kind: 'camp', title: '⛺ BASE CAMP', sub: '100 hours of E2 footage' }) },
    { label: '🚩 Headset flag', run: () => fire({ key: `t${Date.now()}`, kind: 'flag', title: '🚩 10 HEADSETS', sub: 'E2 headsets in the field' }) },
    { label: '🔥 Summit pace', run: () => fire({ key: `t${Date.now()}`, kind: 'pace', title: '🔥 SUMMIT PACE', sub: 'Tomas Home Services · headset 2838 held 3 h/day for a week' }) },
    { label: '🏔️ Org at pace', run: () => fire({ key: `t${Date.now()}`, kind: 'orgpace', title: '🏔️ ORG AT SUMMIT PACE', sub: 'Gecko Green averaged 3 h/day on every headset' }) },
    { label: '📈 Best week', run: () => fire({ key: `t${Date.now()}`, kind: 'record', title: '📈 BEST WEEK EVER', sub: '25.6 hours of E2 footage in one week' }) },
    { label: '🎧 New headset', run: () => pushToast({ key: `t${Date.now()}`, title: 'New E2 online', sub: 'ServiceMaster · headset 2822 just joined the climb' }) },
    { label: '🔢 Odometer roll', run: () => setRollPulse(p => p + 1) },
  ];

  // ---- render ----
  const topBar = (
    <div className="e2-topbar">
      <div className="e2-logo">
        <button className="e2-icon" onClick={() => navigate('/customer-management')} title="Customer Management"><FiArrowLeft /></button>
        🎧 E2 SUMMIT
      </div>
      <div className="e2-tools">
        <button className="e2-icon" onClick={() => setShowTests(s => !s)} title="Preview moments">🎬</button>
        <button className="e2-icon" onClick={() => { setSound((s: boolean) => !s); playTone([660], 0.05); }} title={sound ? 'Mute' : 'Sound on'}>
          {sound ? <FiVolume2 /> : <FiVolumeX />}
        </button>
        <button className="e2-icon" onClick={toggleFullscreen} title="Full screen">{fullscreen ? <FiMinimize /> : <FiMaximize />}</button>
      </div>
    </div>
  );

  if (!data) {
    return (
      <div className="e2-root" ref={rootRef}>
        <div className="e2-stage e2-stage-empty">
          {topBar}
          <div className="e2-empty">
            <div className="e2-empty-title">⛺ Setting up base camp</div>
            <p>{error ?? 'Counting E2 headsets and footage. The first count takes a few seconds.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const hs = data.headsets, tgt = data.target;
  const nextCamp = tgt.camps.find(c => c.next);
  const prevCampAt = [...tgt.camps].reverse().find(c => c.done)?.at ?? 0;
  const campPct = nextCamp ? Math.min(100, Math.max(0, ((display - prevCampAt) / (nextCamp.at - prevCampAt)) * 100)) : 100;
  const medals = ['🥇', '🥈', '🥉'];
  // Boxes follow the board order; within a customer, recording headsets first.
  const byOrg = (org: string) => data.devices.filter(d => d.org === org)
    .sort((a, b) => Number(isRecording(b)) - Number(isRecording(a)) || b.total_hours - a.total_hours);
  const fleet = orgs.flatMap(o => byOrg(o.org));
  const recording = fleet.filter(isRecording).length;
  const box = (d: E2Device) => (
    <i key={d.id} className={`e2-box${isRecording(d) ? ' e2-on' : ''}`}
      title={`${orgLabel(d.org)} · ${d.id.slice(-4)} · last footage ${lastSeen(d.silent_days ?? null, d.last_recording).toLowerCase()}`} />
  );

  return (
    <div className="e2-root" ref={rootRef}>
      <div className="e2-stage">
        <svg className="e2-sky" viewBox="0 0 1280 720" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <defs>
            <linearGradient id="e2Hill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#9d5cf6" /><stop offset="1" stopColor="#6d28d9" /></linearGradient>
          </defs>
          {Array.from({ length: 40 }, (_, i) => (
            <circle key={i} className="e2-star" cx={(i * 173 + 29) % 1280} cy={(i * 61 + 13) % 230} r={i % 5 === 0 ? 2 : 1.2} style={{ animationDelay: `${(i % 7) * 0.4}s` }} />
          ))}
          <path d="M0 720 L0 640 L200 600 L420 520 L640 400 L820 470 L1040 330 L1180 260 L1280 300 L1280 720Z" fill="url(#e2Hill)" opacity="0.28" />
        </svg>

        {topBar}

        <div className="e2-hud">
          <div className="e2-hero">
            <section className="e2-card e2-fleet-card">
              <div className="e2-label">E2 headsets in the field</div>
              <div className="e2-big e2-mint">{hs.customer}</div>
              <div className="e2-boxes" role="img" aria-label={`${recording} of ${fleet.length} headsets recording`}>{fleet.map(box)}</div>
              <div className="e2-caption">
                <span className="e2-mint">{recording} recording</span>
                <span className="e2-dimtext"> · {fleet.length - recording} not recording</span>
              </div>
            </section>

            <section className="e2-card e2-odo-card" ref={odoRef}>
              <div className="e2-label">Hours of E2 footage</div>
              <Odometer value={display} />
              <div className="e2-camp-bar"><i style={{ width: `${campPct}%` }} /></div>
              <div className="e2-caption">
                {nextCamp ? <>⛺ {campName(nextCamp.at) === 'BASE CAMP' ? 'Base Camp' : `${fmt(nextCamp.at)} h camp`} at {fmt(nextCamp.at)} h · <span className="e2-gold">{fmt1(Math.max(0, nextCamp.at - display))} h to go</span></> : <>🏔️ Summit reached</>}
              </div>
            </section>
          </div>

          <section className="e2-board">
            <div className="e2-rows">
              <div className="e2-row e2-row-head">
                <span>#</span><span>Customer</span><span>Headsets recording</span><span>Hours</span><span>Last recording</span>
              </div>
              {orgs.map((o, i) => {
                const silent = !o.last_recording || (o.silent_days ?? 0) >= SILENT_DAYS;
                return (
                  <div key={o.org} ref={el => { rowRefs.current[o.org] = el; }}
                    role="link" tabIndex={0} title={`Open ${orgLabel(o.org)} in Customer Management`}
                    onClick={() => navigate(drillUrl(o.org))}
                    onKeyDown={e => { if (e.key === 'Enter') navigate(drillUrl(o.org)); }}
                    className={`e2-row${i < 3 && o.total_hours > 0 ? ' e2-top3' : ''}`}>
                    <span className="e2-rank">{o.total_hours > 0 && i < 3 ? medals[i] : i + 1}</span>
                    <span className="e2-orgcell"><b>{orgLabel(o.org)}</b></span>
                    <span className="e2-boxes e2-boxes-row">{byOrg(o.org).map(box)}</span>
                    <span className="e2-hours">{o.total_hours ? `${fmt1(o.total_hours)} h` : '—'}</span>
                    <span className={`e2-when${silent ? ' e2-late' : ''}`}>{lastSeen(o.silent_days, o.last_recording)}</span>
                  </div>
                );
              })}
              {!orgs.length && <div className="e2-row"><span /><span className="e2-orgcell"><b>No E2 headsets in customer orgs yet</b></span></div>}
            </div>
          </section>
        </div>

        {toast && (
          <div className="e2-toast" key={toast.key}>
            <span className="e2-toast-ico">🎧</span>
            <span><b>{toast.title}</b><small>{toast.sub}</small></span>
          </div>
        )}

        {celeb && (
          <div className={`e2-celeb e2-celeb-${celeb.kind}`} key={celeb.key} style={{ ['--celeb-ms' as any]: `${CELEB_MS}ms` }}>
            {!reduceMotion() && Array.from({ length: 90 }, (_, i) => (
              <i key={i} className="e2-confetti" style={{ left: `${(i * 37) % 100}%`, background: `hsl(${(i * 47) % 360} 90% 60%)`, animationDelay: `${(i % 9) * 0.09}s` }} />
            ))}
            <div className="e2-celeb-card">
              <div className="e2-celeb-title">{celeb.title}</div>
              <div className="e2-celeb-sub">{celeb.sub}</div>
            </div>
          </div>
        )}

        {showTests && (
          <div className="e2-testbar">
            <span>🎬 Preview moments</span>
            {previews.map(p => <button key={p.label} onClick={p.run}>{p.label}</button>)}
          </div>
        )}
        {error && <div className="e2-stale">Showing the last count · {error}</div>}
      </div>
    </div>
  );
};

export default E2SummitTV;
